import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Coins, Gift, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatNumber } from '../../lib/utils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable modal for sending a Coin Shop gift to another user.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   recipient: { id, username, display_name?, avatar? }  — the gift recipient
 *   targetType?: 'live' | 'post' | 'video'
 *   targetId?: string
 *   onSent?: (gift) => void  — called after a successful send
 */
export default function SendGiftModal({ open, onClose, recipient, targetType, targetId, onSent }) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null); // item.id being sent
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [itemsRes, balRes] = await Promise.all([
          axios.get(`${API}/coin-shop/items`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/coins/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!cancelled) {
          setItems(itemsRes.data);
          setBalance(balRes.data.coins || 0);
        }
      } catch (e) {
        if (!cancelled) toast.error('Failed to load gifts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, token]);

  if (!open || !recipient) return null;

  const handleSend = async (item) => {
    if (balance < item.price) {
      toast.error(`Not enough coins. Need ${formatNumber(item.price)} (≈ $${item.price_usd.toFixed(2)}).`);
      return;
    }
    setSending(item.id);
    try {
      const res = await axios.post(
        `${API}/coin-shop/send-gift`,
        {
          item_id: item.id,
          recipient_id: recipient.id,
          target_type: targetType,
          target_id: targetId,
          message,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${item.name} sent to @${recipient.username}!`);
      setBalance((b) => b - item.price);
      onSent?.(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send gift');
    } finally {
      setSending(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4"
      onClick={() => !sending && onClose()}
      data-testid="send-gift-modal"
    >
      <Card
        className="w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-4 space-y-3 overflow-y-auto">
          {/* Header */}
          <div className="flex items-start justify-between sticky top-0 bg-card z-10 pb-2">
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Gift className="w-5 h-5 text-primary" />
                Send a Gift
              </h3>
              <p className="text-sm text-muted-foreground">
                To <span className="font-medium">@{recipient.username}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Your balance: {formatNumber(balance)} coins
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} disabled={!!sending} data-testid="close-gift-modal">
              <XCircle className="w-5 h-5" />
            </Button>
          </div>

          {/* Optional message */}
          <div>
            <textarea
              rows={2}
              maxLength={200}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a message (optional)"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="gift-message-textarea"
            />
          </div>

          {/* Gift grid */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {items.map((item) => {
                const canAfford = balance >= item.price;
                return (
                  <button
                    key={item.id}
                    disabled={!canAfford || sending === item.id}
                    onClick={() => handleSend(item)}
                    className={`relative rounded-lg overflow-hidden border-2 text-left transition-all ${
                      canAfford
                        ? 'border-transparent hover:border-primary hover:scale-[1.02] active:scale-95'
                        : 'border-transparent opacity-50 cursor-not-allowed'
                    }`}
                    data-testid={`gift-option-${item.id}`}
                  >
                    <div className="aspect-square bg-muted">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-2 bg-card">
                      <p className="text-xs font-semibold truncate">{item.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-bold text-primary flex items-center gap-0.5">
                          <Coins className="w-3 h-3" />
                          {formatNumber(item.price)}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          ${item.price_usd.toFixed(0)}
                        </span>
                      </div>
                    </div>
                    {sending === item.id && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* No balance helper */}
          {!loading && items.length > 0 && balance < Math.min(...items.map((i) => i.price)) && (
            <p className="text-xs text-center text-muted-foreground pt-2">
              Not enough coins.{' '}
              <a href="/coins" className="text-primary underline">
                Buy more
              </a>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
